import React, { useState } from "react";
import { Button, Space, Tag, Card, Row, Col, Empty } from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import DataSelectorModal from "../../../Modal/programmaticSeo/DataSelector/DataSelectorModal";
import "./CampaignDataSelector.css";

const CampaignDataSelector = ({ pageType, value = {}, onChange }) => {
  const [selectorVisible, setSelectorVisible] = useState(false);
  const [selectorType, setSelectorType] = useState(null);

  const dataConfig = {
    comparison: {
      competitors: {
        title: "Select Competitors",
        dataType: "competitors",
        color: "blue",
        maxSelection: 50,
      },
      useCases: {
        title: "Select Use Cases",
        dataType: "useCases",
        color: "green",
        maxSelection: 30,
      },
    },
    integration: {
      tools: {
        title: "Select Integration Tools",
        dataType: "integrations",
        color: "purple",
        maxSelection: 100,
      },
    },
    location: {
      locations: {
        title: "Select Locations",
        dataType: "locations",
        color: "orange",
        maxSelection: 200,
      },
    },
    useCase: {
      useCases: {
        title: "Select Use Cases",
        dataType: "useCases",
        color: "green",
        maxSelection: 50,
      },
    },
    locationUseCase: {
      locations: {
        title: "Select Locations",
        dataType: "locations",
        color: "orange",
        maxSelection: 100,
      },
      useCases: {
        title: "Select Use Cases",
        dataType: "useCases",
        color: "green",
        maxSelection: 50,
      },
    },
    integrationUseCase: {
      integrations: {
        title: "Select Integrations",
        dataType: "integrations",
        color: "blue",
        maxSelection: 100,
      },
      useCases: {
        title: "Select Use Cases",
        dataType: "useCases",
        color: "green",
        maxSelection: 50,
      },
    },
  };

  const currentConfig = dataConfig[pageType] || {};

  const openSelector = (type) => {
    setSelectorType(type);
    setSelectorVisible(true);
  };

  const handleSelectorConfirm = (selectedData) => {
    const newValue = {
      ...value,
      [selectorType]: selectedData,
    };
    onChange(newValue);
    setSelectorVisible(false);
    setSelectorType(null);
  };

  const removeSelectedItem = (type, itemToRemove) => {
    const currentItems = value[type] || [];
    const newItems = currentItems.filter(
      (item) =>
        (item.id || item.slug) !== (itemToRemove.id || itemToRemove.slug)
    );
    const newValue = {
      ...value,
      [type]: newItems,
    };
    onChange(newValue);
  };

  const calculateCombinations = () => {
    if (!pageType || !value) return 0;

    switch (pageType) {
      case "comparison":
        const competitors = value.competitors?.length || 0;
        const useCases = value.useCases?.length || 0;
        return competitors > 0 && useCases > 0 ? competitors * useCases : 0;

      case "integration":
        const tools = value.tools?.length || 0;
        return tools > 1 ? Math.floor((tools * (tools - 1)) / 2) : 0;

      case "location":
        return value.locations?.length || 0;

      case "useCase":
        return value.useCases?.length || 0;

      case "locationUseCase":
        const locations = value.locations?.length || 0;
        const useCasesLoc = value.useCases?.length || 0;
        return locations > 0 && useCasesLoc > 0 ? locations * useCasesLoc : 0;

      case "integrationUseCase":
        const integrations = value.integrations?.length || 0;
        const useCasesInt = value.useCases?.length || 0;
        return integrations > 0 && useCasesInt > 0 ? integrations * useCasesInt : 0;

      default:
        return 0;
    }
  };

  const renderDataSection = (type, config) => {
    const selectedItems = value[type] || [];
    const isEmpty = selectedItems.length === 0;

    return (
      <Card
        size="small"
        title={
          <Space>
            <span>{config.title}</span>
            {selectedItems.length > 0 && (
              <Tag color={config.color}>{selectedItems.length} selected</Tag>
            )}
          </Space>
        }
        extra={
          <Space>
            <Button
              type="primary"
              size="small"
              icon={isEmpty ? <PlusOutlined /> : <EditOutlined />}
              onClick={() => openSelector(type)}
            >
              {isEmpty ? "Select" : "Edit"}
            </Button>
            {!isEmpty && (
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => {
                  const newValue = { ...value };
                  delete newValue[type];
                  onChange(newValue);
                }}
              >
                Clear
              </Button>
            )}
          </Space>
        }
        className="data-selection-card"
      >
        {isEmpty ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span>
                No {config.title.toLowerCase()} selected
                <br />
                <span style={{ fontSize: "12px", color: "#666" }}>
                  Click "Select" to choose from available options
                </span>
              </span>
            }
          />
        ) : (
          <div className="selected-items">
            {selectedItems.map((item, index) => (
              <Tag
                key={item.id || item.slug || index}
                color={config.color}
                closable
                onClose={() => removeSelectedItem(type, item)}
                className="selected-item-tag"
              >
                <Space size="small">
                  <span>{item.name}</span>
                  {item.category && (
                    <span style={{ fontSize: "11px", color: "#666" }}>
                      ({item.category})
                    </span>
                  )}
                </Space>
              </Tag>
            ))}
          </div>
        )}
      </Card>
    );
  };

  const renderCombinationInfo = () => {
    const combinations = calculateCombinations();

    if (combinations === 0) return null;

    return (
      <Card size="small" className="combination-info-card">
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Space>
              <InfoCircleOutlined style={{ color: "#1890ff" }} />
              <span style={{ fontWeight: "600" }}>
                Estimated Page Combinations:
              </span>
              <span
                style={{
                  fontSize: "18px",
                  color: "#1890ff",
                  fontWeight: "600",
                }}
              >
                {combinations.toLocaleString()}
              </span>
            </Space>
          </Col>
          <Col>
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => {
                // Could open a preview modal here
                console.log("Preview combinations");
              }}
            >
              Preview
            </Button>
          </Col>
        </Row>

        {pageType === "comparison" && (
          <div style={{ marginTop: 8, fontSize: "12px", color: "#666" }}>
            {value.competitors?.length || 0} competitors ×{" "}
            {value.useCases?.length || 0} use cases
          </div>
        )}

        {pageType === "integration" && (
          <div style={{ marginTop: 8, fontSize: "12px", color: "#666" }}>
            {value.tools?.length || 0} tools creating {combinations} unique
            pairs
          </div>
        )}
      </Card>
    );
  };

  if (!pageType) {
    return (
      <Card size="small">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Please select a page type first"
        />
      </Card>
    );
  }

  return (
    <div className="campaign-data-selector">
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        {Object.entries(currentConfig).map(([type, config]) => (
          <div key={type}>{renderDataSection(type, config)}</div>
        ))}

        {renderCombinationInfo()}
      </Space>

      {/* Data Selector Modal */}
      <DataSelectorModal
        visible={selectorVisible}
        onCancel={() => {
          setSelectorVisible(false);
          setSelectorType(null);
        }}
        onConfirm={handleSelectorConfirm}
        dataType={selectorType && currentConfig[selectorType]?.dataType}
        title={selectorType && currentConfig[selectorType]?.title}
        selectedItems={selectorType ? value[selectorType] || [] : []}
        maxSelection={selectorType && currentConfig[selectorType]?.maxSelection}
      />
    </div>
  );
};

export default CampaignDataSelector;
